import { useCallback } from 'react';

import { type MeResponse, MeResponseSchema } from '../../api';
import { useApiClient } from '../../lib/api/api-context';
import { type ApiQueryResult, useApiQuery } from '../../lib/api/use-api-query';
import { useAppVisible } from '../../lib/telegram/use-app-visible';

/** Master X8, technical spec §4: `GET /me` is refreshed every 5 s while Home is visible. */
const ME_REFRESH_INTERVAL_MS = 5_000;

/**
 * Home data from `GET /me` (M1 §5.1), parsed with the contract schema, refreshed every 5 s while
 * the app is visible (M7 R1–R3). Only Home calls it, so leaving Home stops the polling (M7 R4).
 * A failed refresh keeps the last data (`refreshError`, M7 R7); a 401 `TOKEN_EXPIRED` is renewed
 * once by the API client before the poll fails (S2 R18, M7 R8).
 */
export function useMe(): ApiQueryResult<MeResponse> {
  const api = useApiClient();
  const visible = useAppVisible();
  const load = useCallback(
    (signal: AbortSignal) => api.get('/me', MeResponseSchema, signal),
    [api],
  );
  return useApiQuery(load, { poll: { intervalMs: ME_REFRESH_INTERVAL_MS, active: visible } });
}
